import React, { useState } from 'react';
import { Container, Row, Col, Button } from 'reactstrap';
import {
  FaCheckCircle,
  FaCopy,
  FaExclamationTriangle,
  FaShieldAlt,
  FaCoins,
  FaGift,
  FaVoteYea,
  FaChartLine,
  FaTwitter,
  FaTelegram,
  FaGlobe,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import './PresaleDetails.css';

const PresaleDetails = () => {
  const presaleAddress = '0x09c27A2077Cb713b083D97630dE866B72fF9b4c1';
  const [copied, setCopied] = useState(false);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(presaleAddress);
    setCopied(true);
    toast.success('Presale address copied to clipboard!');
    setTimeout(() => setCopied(false), 3000);
  };

  const benefits = [
    {
      icon: <FaCoins />,
      title: 'Access to MCGP Ecosystem',
      description: 'Full access to all MCGP ecosystem products and services',
    },
    {
      icon: <FaVoteYea />,
      title: 'Governance Rights',
      description: 'Participate in platform governance and decision-making',
    },
    {
      icon: <FaChartLine />,
      title: 'Staking Rewards',
      description: 'Earn passive income through our staking program',
    },
    {
      icon: <FaGift />,
      title: 'Exclusive Benefits',
      description:
        'Future airdrops, partner benefits & early access to new features',
    },
  ];

  return (
    <>
      {/* Why Hold MCGP Section */}
      <section className="presale-benefits" id="benefits">
        <Container>
          <Row className="mb-5">
            <Col lg={12} className="text-center">
              <h2 className="section-title" data-aos="fade-up">
                Why Hold <span className="gradient-text">MCGP</span>?
              </h2>
              <p
                className="section-subtitle"
                data-aos="fade-up"
                data-aos-delay="100"
              >
                The MCGP token fuels every transaction, service, and innovation
                within our ecosystem
              </p>
            </Col>
          </Row>

          <Row>
            {benefits.map((benefit, index) => (
              <Col
                lg={6}
                md={6}
                key={index}
                data-aos="fade-up"
                data-aos-delay={index * 100}
              >
                <div className="benefit-card">
                  <div className="benefit-icon">{benefit.icon}</div>
                  <h4>{benefit.title}</h4>
                  <p>{benefit.description}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* How to Participate Section */}
      <section className="presale-participate" id="participate">
        <Container>
          <Row className="mb-5">
            <Col lg={12} className="text-center">
              <h2 className="section-title" data-aos="fade-up">
                How to <span className="gradient-text">Participate</span>
              </h2>
              <p
                className="section-subtitle"
                data-aos="fade-up"
                data-aos-delay="100"
              >
                Follow these simple steps to secure your MCGP tokens
              </p>
            </Col>
          </Row>

          <Row className="justify-content-center">
            <Col lg={10}>
              {/* Step 1 */}
              <div className="participate-step" data-aos="fade-right">
                <div className="step-number">1</div>
                <div className="step-content">
                  <h4>Copy the Official Presale Address</h4>
                  <p className="mb-3">
                    Ensure you use the verified MCGP presale address only.
                    Double-check before sending.
                  </p>
                  <div className="presale-address-box">
                    <div className="address-display">
                      <FaShieldAlt className="shield-icon" />
                      <span className="address-text">{presaleAddress}</span>
                    </div>
                    <Button className="copy-btn" onClick={copyToClipboard}>
                      {copied ? <FaCheckCircle /> : <FaCopy />}
                      {copied ? 'Copied!' : 'Copy'}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Step 2 */}
              <div className="participate-step" data-aos="fade-left">
                <div className="step-number">2</div>
                <div className="step-content">
                  <h4>Send USDC/USDT to the Address</h4>
                  <p>
                    Send your contribution using one of the following options:
                  </p>
                  <div className="network-options">
                    <div className="network-card">
                      <div className="network-badge">Option 1</div>
                      <h5>USDC on Sonic Network</h5>
                      <p>Recommended for faster transactions</p>
                    </div>
                    <div className="network-card">
                      <div className="network-badge">Option 2</div>
                      <h5>USDT on Binance Smart Chain</h5>
                      <p>Alternative network option</p>
                    </div>
                  </div>
                  <div className="contribution-limits">
                    <div className="limit-item">
                      <span className="limit-label">Minimum:</span>
                      <span className="limit-value">$10</span>
                    </div>
                    <div className="limit-item">
                      <span className="limit-label">Maximum:</span>
                      <span className="limit-value">$1,000 per wallet</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 3 */}
              <div className="participate-step" data-aos="fade-right">
                <div className="step-number">3</div>
                <div className="step-content">
                  <h4>Receive Your MCGP Tokens Automatically</h4>
                  <p>
                    Tokens will be automatically distributed to the same wallet
                    address you sent from after the presale concludes on{' '}
                    <strong>November 30, 2025</strong>.
                  </p>
                  <div className="info-note">
                    <FaCheckCircle className="me-2" />
                    <span>
                      No additional action required — just sit back and wait for
                      distribution!
                    </span>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Important Warnings */}
      <section className="presale-warnings">
        <Container>
          <Row className="justify-content-center">
            <Col lg={10}>
              <div className="warning-box" data-aos="zoom-in">
                <div className="warning-header">
                  <FaExclamationTriangle className="warning-icon" />
                  <h4>Important Security Notice</h4>
                </div>
                <ul className="warning-list">
                  <li>
                    <FaCheckCircle className="me-2" />
                    Only send USDC/USDT from a wallet you control (not from
                    exchanges)
                  </li>
                  <li>
                    <FaCheckCircle className="me-2" />
                    Tokens sent from incompatible networks or exchanges may be
                    permanently lost
                  </li>
                  <li>
                    <FaCheckCircle className="me-2" />
                    Double-check the presale address before sending — we will
                    never DM you first
                  </li>
                  <li>
                    <FaCheckCircle className="me-2" />
                    Beware of scammers — only use the official address displayed
                    on this page
                  </li>
                </ul>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Community Section */}
      <section className="presale-community">
        <Container>
          <Row className="mb-4">
            <Col lg={12} className="text-center">
              <h2 className="section-title" data-aos="fade-up">
                Join the <span className="gradient-text">Community</span>
              </h2>
              <p
                className="section-subtitle"
                data-aos="fade-up"
                data-aos-delay="100"
              >
                Follow MCGP for live presale updates and announcements
              </p>
            </Col>
          </Row>

          <Row className="justify-content-center">
            <Col lg={8}>
              <div
                className="community-links"
                data-aos="fade-up"
                data-aos-delay="200"
              >
                <a
                  href="https://mcgpchain.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="community-link"
                >
                  <FaGlobe />
                  <span>Website</span>
                </a>
                <a
                  href="https://x.com/MCGPchain"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="community-link"
                >
                  <FaTwitter />
                  <span>X/Twitter</span>
                </a>
                <a
                  href="https://t.me/mcgpchain"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="community-link"
                >
                  <FaTelegram />
                  <span>Telegram</span>
                </a>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default PresaleDetails;
